import { buildCollectUrl, isBrowser, logDebug } from '../utils';
import { SDK_VERSION } from '../version';
import type { CollectRequestBody, TrackingEnvironment, TrackingPayload } from '../types';

export type TransportOptions = {
  endpoint: string;
  productId: string;
  environment: TrackingEnvironment;
  debug: boolean;
  maxRetries: number;
};

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function sendEvents(
  events: TrackingPayload[],
  options: TransportOptions,
  preferBeacon = false
): Promise<boolean> {
  if (!events.length) return true;

  const body: CollectRequestBody = {
    product_id: options.productId,
    environment: options.environment,
    sdk_version: SDK_VERSION,
    sent_at: Date.now(),
    events,
  };

  const url = buildCollectUrl(options.endpoint, options.productId);
  const payload = JSON.stringify(body);
  logDebug(options.debug, 'send', { url, count: events.length, preferBeacon });

  if (preferBeacon && isBrowser() && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([payload], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return true;
    } catch {
      /* fallthrough to fetch */
    }
  }

  let attempt = 0;
  while (attempt <= options.maxRetries) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
        mode: 'cors',
        credentials: 'omit',
      });
      if (res.ok) return true;
      logDebug(options.debug, 'send failed status', res.status);
    } catch (err) {
      logDebug(options.debug, 'send error', err);
    }
    attempt += 1;
    if (attempt <= options.maxRetries) {
      await sleep(Math.min(1000 * 2 ** (attempt - 1), 8000));
    }
  }
  return false;
}
